<template>
  <div class="admin-page">
    <div class="admin-toolbar">
      <div class="admin-toolbar__actions">
        <el-input
          v-model="searchQuery"
          placeholder="搜索测评名称..."
          class="search-input"
          clearable
          @keyup.enter="handleSearch"
          @clear="handleSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
        <el-select
          v-model="removedFilter"
          class="filter-select"
          @change="handleSearch"
        >
          <el-option :value="0" label="删除：全部" />
          <el-option :value="1" label="未删除" />
          <el-option :value="2" label="已删除" />
        </el-select>
        <el-select
          v-model="stateFilter"
          class="filter-select"
          @change="handleSearch"
        >
          <el-option :value="0" label="上架：全部" />
          <el-option :value="1" label="上架" />
          <el-option :value="2" label="已下架" />
        </el-select>
        <el-select
          v-model="auditFilter"
          class="filter-select"
          @change="handleSearch"
        >
          <el-option :value="0" label="审核：全部" />
          <el-option :value="1" label="待审核" />
          <el-option :value="2" label="已通过" />
          <el-option :value="3" label="已驳回" />
        </el-select>
        <el-select
          v-model="tagIdFilter"
          class="filter-select"
          clearable
          placeholder="内容标签"
          @change="handleSearch"
        >
          <el-option
            v-for="tag in examTags"
            :key="tag.tagId"
            :label="tag.name"
            :value="tag.tagId"
          />
        </el-select>
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button type="primary" @click="createTest">新增测评</el-button>
      </div>
    </div>

    <el-collapse v-model="statusHelpOpen" class="status-help">
      <el-collapse-item name="help">
        <template #title>
          <span class="status-help__title">删除 × 上架 × 审核 状态组合说明</span>
        </template>
        <p class="status-help__note">
          <code>removed</code> 逻辑删除、<code>state</code> 上下架、<code>audit_status</code> 审核，三列独立。
          C 端列表只展示「未删除 + 上架 + 审核通过」。资料可见性只看删除和上下架，不看审核。
        </p>
        <el-table
          :data="statusCombos"
          border
          size="small"
          class="status-help__table"
          max-height="420"
          :row-class-name="comboRowClassName"
        >
          <el-table-column prop="removed" label="删除 removed" width="120" />
          <el-table-column prop="state" label="上架 state" width="120" />
          <el-table-column prop="audit" label="审核 audit_status" width="150" />
          <el-table-column prop="meaning" label="含义" min-width="180" />
          <el-table-column prop="cList" label="C 端列表" width="90" />
          <el-table-column prop="visibility" label="资料可见性条件" min-width="150" />
          <el-table-column prop="action" label="本页怎么改" min-width="180" />
        </el-table>
      </el-collapse-item>
    </el-collapse>

    <el-table
      :data="exams"
      v-loading="loading"
      style="width: 100%"
      :row-class-name="tableRowClassName"
    >
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column label="缩略图" width="100">
        <template #default="scope">
          <el-image
            v-if="scope.row.thumb"
            :src="scope.row.thumb"
            fit="cover"
            style="width: 60px; height: 60px; border-radius: 4px"
          />
          <span v-else class="no-thumb">—</span>
        </template>
      </el-table-column>
      <el-table-column prop="examName" label="测评名称" min-width="160" show-overflow-tooltip />
      <el-table-column prop="examSname" label="英文简称" width="120" show-overflow-tooltip />
      <el-table-column label="内容标签" min-width="220">
        <template #default="s">
          <ContentTagSelect
            :model-value="tagIdsFromRow(s.row)"
            scope="exam"
            placeholder="加减标签"
            @update:model-value="(ids) => saveRowTags(s.row, ids)"
          />
        </template>
      </el-table-column>
      <el-table-column prop="profile" label="简介" min-width="200" show-overflow-tooltip />
      <el-table-column label="费用" width="140">
        <template #default="scope">
          <template v-if="scope.row.free === 1">
            <el-tag type="success" effect="plain" size="small">免费</el-tag>
          </template>
          <template v-else-if="scope.row.fee === 0 && scope.row.originFee > 0">
            <span class="price-original">¥{{ (scope.row.originFee / 100).toFixed(2) }}</span>
            <el-tag type="danger" effect="plain" size="small">限时免费</el-tag>
          </template>
          <template v-else-if="scope.row.fee > 0 && scope.row.originFee > scope.row.fee">
            <span>¥{{ (scope.row.fee / 100).toFixed(2) }}</span>
            <span class="price-original">¥{{ (scope.row.originFee / 100).toFixed(2) }}</span>
            <el-tag type="warning" effect="plain" size="small">限时优惠</el-tag>
          </template>
          <el-tag v-else-if="scope.row.fee === 0" type="success" size="small">免费</el-tag>
          <span v-else>¥{{ (scope.row.fee / 100).toFixed(2) }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="questionCount" label="题数" width="80" />
      <el-table-column label="审核状态" width="100">
        <template #default="scope">
          <el-tag :type="auditTagType(scope.row.auditStatus)" size="small">
            {{ auditStatusText(scope.row.auditStatus) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="删除" width="90">
        <template #default="scope">
          <el-tag :type="Number(scope.row.removed) === 1 ? 'danger' : 'success'" size="small">
            {{ Number(scope.row.removed) === 1 ? '已删除' : '正常' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="上架" width="160">
        <template #default="scope">
          <el-tag :type="Number(scope.row.state) === 1 ? 'success' : 'info'" size="small">
            {{ Number(scope.row.state) === 1 ? '上架' : '已下架' }}
          </el-tag>
          <el-button
            v-if="Number(scope.row.state) === 1"
            size="small"
            link
            type="warning"
            :loading="stateSavingId === scope.row.id"
            @click="setState(scope.row, 0)"
          >下架</el-button>
          <el-button
            v-else
            size="small"
            link
            type="success"
            :loading="stateSavingId === scope.row.id"
            @click="setState(scope.row, 1)"
          >上架</el-button>
        </template>
      </el-table-column>
      <el-table-column label="官方认证" width="110">
        <template #default="scope">
          <el-switch
            :model-value="Number(scope.row.officialCertified) === 1"
            :loading="officialSavingId === scope.row.id"
            @change="(v) => onOfficialChange(scope.row, v)"
          />
        </template>
      </el-table-column>
      <el-table-column label="佣金档位" width="200">
        <template #default="scope">
          <el-select
            v-if="canPresetExamCommission(scope.row)"
            :model-value="scope.row.platformCommissionPct || 0"
            size="small"
            style="width: 180px"
            @change="(v) => onCommissionChange('exam', scope.row.id, v, scope.row)"
          >
            <el-option
              v-for="opt in COMMISSION_TIER_OPTIONS"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column prop="createdAt" label="创建时间" width="170">
        <template #default="scope">
          {{ formatDate(scope.row.createdAt) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="560" fixed="right">
        <template #default="scope">
          <el-button size="small" type="primary" @click="viewTest(scope.row.id)">查看</el-button>
          <el-button size="small" @click="previewExam(scope.row.id)">预览</el-button>
          <el-button size="small" @click="takeExam(scope.row.id)">试测</el-button>
          <el-button size="small" @click="viewExamResults(scope.row.id)">测试结果</el-button>
          <el-button size="small" type="info" @click="exportYaml(scope.row.id)">YAML</el-button>
          <el-button size="small" type="warning" @click="editTest(scope.row.id)">编辑</el-button>
          <el-button
            v-if="Number(scope.row.removed) === 1"
            size="small"
            type="success"
            :loading="removedSavingId === scope.row.id"
            @click="setRemoved(scope.row, 0)"
          >恢复</el-button>
          <el-button
            v-else
            size="small"
            type="danger"
            @click="deleteTest(scope.row)"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <AdminPagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />

    <el-dialog v-model="deleteDialogVisible" title="确认删除" width="400px">
      <span>将标记为已删除，前台列表不再展示，可在本页筛选「已删除」后恢复。</span>
      <template #footer>
        <el-button @click="deleteDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="confirmDelete">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import axios from 'axios'
import AdminPagination from '@/components/AdminPagination.vue'
import {
  COMMISSION_TIER_OPTIONS,
  canPresetExamCommission,
  setContentCommissionTier
} from '@/utils/commissionTier'
import { bumpExamListCache } from '@/utils/listPageCache'
import ContentTagSelect from '@/components/ContentTagSelect.vue'
import { bindContentTags, tagIdsFromRow } from '@/utils/contentTags'

export default {
  name: 'ExamManagement',
  components: { Search, AdminPagination, ContentTagSelect },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const exams = ref([])
    const searchQuery = ref('')
    const removedFilter = ref(0)
    const stateFilter = ref(0)
    const auditFilter = ref(0)
    const tagIdFilter = ref(null)
    const examTags = ref([])
    const currentPage = ref(1)
    const pageSize = ref(20)
    const total = ref(0)
    const deleteDialogVisible = ref(false)
    const examToDelete = ref(null)
    const officialSavingId = ref(0)
    const removedSavingId = ref(0)
    const stateSavingId = ref(0)
    const statusHelpOpen = ref(['help'])
    const statusCombos = (() => {
      const auditLabel = { 0: '0 待审核', 1: '1 已通过', 2: '2 已驳回' }
      const auditMeaning = { 0: '待审核', 1: '审核通过', 2: '已驳回' }
      const rows = []
      for (const removed of [0, 1]) {
        for (const state of [1, 0]) {
          for (const audit of [1, 0, 2]) {
            const notDel = removed === 0
            const onShelf = state === 1
            rows.push({
              removed: removed === 0 ? '0 正常' : '1 已删除',
              state: state === 1 ? '1 上架' : '0 已下架',
              audit: auditLabel[audit],
              meaning: [
                notDel ? '未删除' : '已删除',
                onShelf ? '上架' : '已下架',
                auditMeaning[audit]
              ].join('、'),
              cList: notDel && onShelf && audit === 1 ? '显示' : '不显示',
              visibility: notDel && onShelf ? '有效，按成绩比较' : '已失效，匹配时跳过',
              action: [
                removed === 1 ? '恢复' : '删除',
                state === 1 ? '下架' : '上架',
                '编辑页改审核'
              ].join(' / '),
              live: notDel && onShelf && audit === 1
            })
          }
        }
      }
      return rows
    })()

    const getAuthHeaders = () => {
      const token = localStorage.getItem('jwt_token')
      return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }

    const formatDate = (timestamp) => {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('zh-CN')
    }

    const auditStatusText = (status) => {
      const map = { 0: '待审核', 1: '已通过', 2: '已驳回' }
      return map[status] ?? '未知'
    }

    const auditTagType = (status) => {
      const map = { 0: 'warning', 1: 'success', 2: 'danger' }
      return map[status] ?? 'info'
    }

    const tableRowClassName = ({ row }) => {
      return Number(row.removed) === 1 ? 'exam-row-deleted' : ''
    }

    const comboRowClassName = ({ row }) => {
      return row.live ? 'status-help__row--live' : ''
    }

    const fetchExams = async () => {
      try {
        loading.value = true
        const body = {
          page: currentPage.value,
          pageSize: pageSize.value,
          fromId: 0,
          removedFilter: removedFilter.value,
          stateFilter: stateFilter.value,
          auditFilter: auditFilter.value,
          adminView: 1
        }
        if (searchQuery.value.trim()) {
          body.examName = searchQuery.value.trim()
        }
        if (tagIdFilter.value) {
          body.tagId = Number(tagIdFilter.value)
        }
        const response = await axios.post('/api/v1/exam/list', body, {
          headers: getAuthHeaders()
        })
        if (response.data.code === 200) {
          exams.value = response.data.data?.exams || []
          total.value = response.data.data?.total || 0
        } else {
          ElMessage.error(response.data.message || '获取测评列表失败')
        }
      } catch (error) {
        console.error('Error fetching exams:', error)
        ElMessage.error('获取测评列表失败')
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      currentPage.value = 1
      fetchExams()
    }

    const handleSizeChange = () => {
      currentPage.value = 1
      fetchExams()
    }

    const handlePageChange = () => {
      fetchExams()
    }

    const createTest = () => {
      router.push('/admin/exam/create')
    }

    const viewTest = (id) => {
      router.push(`/admin/exam/${id}`)
    }

    const previewExam = (id) => {
      router.push(`/exam/${id}`)
    }

    const takeExam = (id) => {
      router.push(`/exam/take/${id}`)
    }

    const viewExamResults = (id) => {
      router.push({ path: '/admin/exam-results', query: { examId: String(id) } })
    }

    const editTest = (id) => {
      router.push(`/admin/exam/edit/${id}`)
    }

    const exportYaml = (id) => {
      router.push(`/admin/exam/${id}/yaml`)
    }

    const deleteTest = (row) => {
      examToDelete.value = row.id
      deleteDialogVisible.value = true
    }

    const setState = async (row, next) => {
      const prev = Number(row.state) === 1 ? 1 : 0
      stateSavingId.value = row.id
      try {
        const response = await axios.post(
          '/api/v1/admin/exam/state',
          { examId: row.id, state: next },
          { headers: getAuthHeaders() }
        )
        if (response.data.code === 200) {
          row.state = next
          ElMessage.success(response.data.message || (next ? '已上架' : '已下架'))
          bumpExamListCache()
          if (stateFilter.value !== 0) {
            fetchExams()
          }
        } else {
          row.state = prev
          ElMessage.error(response.data.message || '操作失败')
        }
      } catch (error) {
        row.state = prev
        ElMessage.error('操作失败')
      } finally {
        stateSavingId.value = 0
      }
    }

    const setRemoved = async (row, next) => {
      const prev = Number(row.removed) === 1 ? 1 : 0
      removedSavingId.value = row.id
      try {
        const response = await axios.post(
          '/api/v1/admin/exam/removed',
          { examId: row.id, removed: next },
          { headers: getAuthHeaders() }
        )
        if (response.data.code === 200) {
          row.removed = next
          ElMessage.success(response.data.message || (next ? '已删除' : '已恢复'))
          bumpExamListCache()
          if (removedFilter.value !== 0) {
            fetchExams()
          }
        } else {
          row.removed = prev
          ElMessage.error(response.data.message || '操作失败')
        }
      } catch (error) {
        row.removed = prev
        ElMessage.error('操作失败')
      } finally {
        removedSavingId.value = 0
      }
    }

    const confirmDelete = async () => {
      try {
        const response = await axios.post(
          '/api/v1/admin/exam/removed',
          { examId: examToDelete.value, removed: 1 },
          { headers: getAuthHeaders() }
        )
        if (response.data.code === 200) {
          ElMessage.success(response.data.message || '已标记为删除')
          bumpExamListCache()
          deleteDialogVisible.value = false
          examToDelete.value = null
          fetchExams()
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch (error) {
        console.error('Error deleting exam:', error)
        ElMessage.error('删除失败')
      }
    }

    const onOfficialChange = async (row, enabled) => {
      const next = enabled ? 1 : 0
      const prev = Number(row.officialCertified) === 1 ? 1 : 0
      officialSavingId.value = row.id
      try {
        const response = await axios.post(
          '/api/v1/admin/exam/official',
          { examId: row.id, officialCertified: next },
          { headers: getAuthHeaders() }
        )
        if (response.data.code === 200) {
          row.officialCertified = next
          ElMessage.success(response.data.message || (next ? '已设为官方认证' : '已取消官方认证'))
          bumpExamListCache()
        } else {
          row.officialCertified = prev
          ElMessage.error(response.data.message || '设置失败')
        }
      } catch (error) {
        row.officialCertified = prev
        ElMessage.error('设置官方认证失败')
      } finally {
        officialSavingId.value = 0
      }
    }

    const onCommissionChange = async (contentType, contentId, pct, row) => {
      const prev = row.platformCommissionPct || 0
      try {
        const data = await setContentCommissionTier(contentType, contentId, pct)
        if (data.code === 200) {
          row.platformCommissionPct = pct
          ElMessage.success('佣金档位已更新')
        } else {
          row.platformCommissionPct = prev
          ElMessage.error(data.message || '更新失败')
        }
      } catch (error) {
        row.platformCommissionPct = prev
        ElMessage.error('更新佣金档位失败')
      }
    }

    const fetchExamTags = async () => {
      try {
        const response = await axios.post('/api/v1/content/tag/list', { scope: 'exam' })
        if (response.data.code === 200) {
          examTags.value = response.data.data?.tags || []
        }
      } catch {
        examTags.value = []
      }
    }

    const saveRowTags = async (row, ids) => {
      const prev = tagIdsFromRow(row)
      row.tagIds = ids || []
      try {
        const data = await bindContentTags('exam', row.id, ids)
        if (data.code === 200) {
          ElMessage.success('标签已更新')
          bumpExamListCache()
        } else {
          row.tagIds = prev
          ElMessage.error(data.message || '更新标签失败')
        }
      } catch (error) {
        row.tagIds = prev
        ElMessage.error(error.response?.data?.message || '更新标签失败')
      }
    }

    onMounted(() => {
      fetchExamTags()
      fetchExams()
    })

    return {
      COMMISSION_TIER_OPTIONS,
      canPresetExamCommission,
      onCommissionChange,
      onOfficialChange,
      officialSavingId,
      removedSavingId,
      stateSavingId,
      loading,
      exams,
      searchQuery,
      removedFilter,
      stateFilter,
      auditFilter,
      tagIdFilter,
      examTags,
      statusHelpOpen,
      statusCombos,
      currentPage,
      pageSize,
      total,
      deleteDialogVisible,
      formatDate,
      auditStatusText,
      auditTagType,
      tableRowClassName,
      comboRowClassName,
      tagIdsFromRow,
      saveRowTags,
      handleSearch,
      handleSizeChange,
      handlePageChange,
      createTest,
      viewTest,
      previewExam,
      takeExam,
      viewExamResults,
      exportYaml,
      editTest,
      deleteTest,
      setRemoved,
      setState,
      confirmDelete
    }
  }
}
</script>

<style scoped>


.price-original {
  text-decoration: line-through;
  color: #909399;
  margin-right: 4px;
  font-size: 12px;
}

.no-thumb {
  color: #c0c4cc;
}

:deep(.exam-row-deleted) {
  color: #909399;
}

.status-help {
  margin-bottom: 16px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;
}

.status-help__title {
  font-weight: 600;
}

.status-help__note {
  margin: 0 0 10px;
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
}

.status-help__note code {
  font-size: 12px;
}

.status-help__table {
  width: 100%;
}

:deep(.status-help__row--live) {
  --el-table-tr-bg-color: #f0f9eb;
}


</style>
